import React from 'react';
import { useTheme } from '../theme';

/** Jaktverktygsknapp i den ovala bottenmenyn: linjeikoner utan egen yta. På = orange ikon i en
 *  rundad markering med orange kant, av = neutral ikon (med snedstreck om knappen kan stängas av). */
export function HuntToolbarButton({
  onClick, title, active, children, tall, amber, ink = 'currentColor', size = 'md', noStrike, onHoldStart, onHoldEnd,
}: {
  onClick: () => void;
  /** I2: håll in knappen (≥ 450 ms) – t.ex. walkie-talkien spelar in ett röstmeddelande; släpp avslutar. Klicket uteblir då. */
  onHoldStart?: () => void;
  onHoldEnd?: () => void;
  title: string;
  active: boolean;
  children: React.ReactNode;
  tall?: boolean;
  amber: string;
  /** Färg på ikonen när knappen inte är markerad (temats textfärg). */
  ink?: string;
  /** lg: jaktmarkens karta — knapparna delar på menyns bredd. */
  size?: 'md' | 'sm' | 'lg';
  /** Ingen diagonal när knappen är av (åtgärder och paneler, t.ex. avsluta / inbjudan / chatt). */
  noStrike?: boolean;
}) {
  const dim = size === 'sm' ? 34 : size === 'lg' ? 44 : 40;
  // Samma höjd med och utan nivåstaplar (tall), så menyn inte hoppar när mikrofonen slås på.
  const h = tall && size === 'sm' ? 44 : dim;
  const on = active;
  const fg = on ? amber : ink;
  const hold = React.useRef<{ timer: ReturnType<typeof setTimeout> | null; held: boolean }>({ timer: null, held: false });
  const endHold = () => {
    if (hold.current.timer) { clearTimeout(hold.current.timer); hold.current.timer = null; }
    if (hold.current.held) onHoldEnd?.();
  };
  const holdProps = onHoldStart ? {
    onPointerDown: () => {
      hold.current.held = false;
      hold.current.timer = setTimeout(() => { hold.current.timer = null; hold.current.held = true; onHoldStart(); }, 450);
    },
    onPointerUp: endHold,
    onPointerCancel: endHold,
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  } : {};
  return (
    <button
      type="button"
      onClick={() => {
        if (hold.current.held) { hold.current.held = false; return; }
        onClick();
      }}
      {...holdProps}
      title={title}
      aria-label={title}
      aria-pressed={noStrike ? undefined : active}
      style={{
        position: 'relative',
        pointerEvents: 'auto', // klickbar även när raden runt släpper igenom kartan
        width: size === 'lg' ? undefined : dim,
        flex: size === 'lg' ? '1 1 0' : `0 1 ${dim}px`, // krymper i smala fönster i stället för att svämma över
        minWidth: size === 'lg' ? 34 : 28,
        maxWidth: size === 'lg' ? 60 : undefined,
        minHeight: h,
        height: h,
        borderRadius: 999,
        border: `1.5px solid ${on ? amber : 'transparent'}`,
        background: on ? `color-mix(in srgb, ${amber} 14%, transparent)` : 'transparent',
        color: fg,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: tall ? 'column' : undefined,
        alignItems: 'center',
        justifyContent: 'center',
        gap: tall ? 1 : undefined,
        boxSizing: 'border-box',
        padding: 0,
        fontFamily: 'inherit',
        transition: 'background 0.15s, border-color 0.15s, color 0.15s',
        WebkitTapHighlightColor: 'transparent',
        ...(onHoldStart ? { WebkitTouchCallout: 'none', userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'manipulation' } as React.CSSProperties : {}),
      }}
    >
      <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: tall ? 'column' : undefined, color: fg, opacity: on || noStrike ? 1 : 0.72 }}>
        {children}
      </span>
      {!active && !noStrike && (
        <svg aria-hidden style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 2 }} viewBox="0 0 28 28" width={28} height={28}>
          <line x1={5} y1={23} x2={23} y2={5} stroke={ink} strokeOpacity={0.8} strokeWidth={2} strokeLinecap="round" />
        </svg>
      )}
    </button>
  );
}

/** Bakgrund för den flytande ovala jaktmenyn: temats bakgrund, lätt genomskinlig och frostad. */
export function huntBarSurface(bg: string, border: string): React.CSSProperties {
  return {
    background: `color-mix(in srgb, ${bg} 76%, transparent)`,
    backdropFilter: 'blur(18px) saturate(1.6)',
    WebkitBackdropFilter: 'blur(18px) saturate(1.6)',
    border: `1px solid ${border}`,
    borderRadius: 999,
    boxShadow: '0 8px 28px rgba(0,0,0,0.28), 0 1px 3px rgba(0,0,0,0.18)',
  };
}

/** Rund, frostad kartknapp (hamburgare, kompass, stäng) i samma stil som jaktmenyn; aktiv = orange kant. */
export function MapRoundButton({ onClick, title, active, children, size = 46, ariaExpanded, ariaControls }: {
  onClick: () => void; title: string; active?: boolean; children: React.ReactNode; size?: number;
  ariaExpanded?: boolean; ariaControls?: string;
}) {
  const T = useTheme();
  return (
    <button type="button" onClick={onClick} title={title} aria-label={title} aria-pressed={active}
      aria-expanded={ariaExpanded} aria-controls={ariaControls}
      style={{
        ...huntBarSurface(T.bg, T.border),
        ...(active ? { border: `1.5px solid ${T.amber}`, background: `color-mix(in srgb, ${T.amber} 16%, color-mix(in srgb, ${T.bg} 80%, transparent))` } : null),
        width: size, height: size, padding: 0, flexShrink: 0, cursor: 'pointer', boxSizing: 'border-box',
        color: active ? T.amber : T.text, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'inherit', WebkitTapHighlightColor: 'transparent', transition: 'border-color 0.15s, color 0.15s',
      }}>
      {children}
    </button>
  );
}

/** Hamburgarikon i linjestil. */
export function IconMenuLines({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

export type CameraSource = 'back' | 'selfie' | 'glasses' | 'drone';

/**
 * Väljare för vilken kamera som delas i jakten, ovanför jaktmenyn (samma frostade stil).
 * Stängs när man väljer något eller trycker utanför. Aktiv källa har orange kant.
 */
export function CameraSourcePicker({ open, onClose, active, glassesAvailable, onSelect, onStop, bottom, onRecordVideo }: {
  open: boolean; onClose: () => void; active: CameraSource | null; glassesAvailable: boolean;
  onSelect: (src: CameraSource) => void; onStop: () => void; bottom: string;
  /** I2: spela in en video till jaktchatten (och markens album) i stället för att sända live. */
  onRecordVideo?: () => void;
}) {
  const T = useTheme();
  if (!open) return null;
  const icon = (d: React.ReactNode) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{d}</svg>
  );
  const items: { src: CameraSource; label: string; hint?: string; disabled?: boolean; icon: React.ReactNode }[] = [
    { src: 'back', label: 'Kamera', hint: 'Telefonens bakre kamera', icon: icon(<><rect x="2" y="6" width="20" height="14" rx="2.5" /><circle cx="12" cy="13" r="3.5" /><path d="M8 6l1.5-2h5L16 6" /></>) },
    { src: 'selfie', label: 'Selfie', hint: 'Främre kameran', icon: icon(<><circle cx="12" cy="8" r="3.5" /><path d="M5 20c1.2-3.5 4-5 7-5s5.8 1.5 7 5" /></>) },
    ...(glassesAvailable ? [{ src: 'glasses' as const, label: 'Meta-glasögon', hint: 'Glasögonens kamera', icon: icon(<><path d="M2 9h20" /><rect x="3" y="9" width="7" height="6.5" rx="2.5" /><rect x="14" y="9" width="7" height="6.5" rx="2.5" /><path d="M10 11.5c1.2-1 2.8-1 4 0" /></>) }] : []),
    { src: 'drone', label: 'Drönare', hint: 'Kommer snart', disabled: true, icon: icon(<><circle cx="5.5" cy="5.5" r="2.5" /><circle cx="18.5" cy="5.5" r="2.5" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /><rect x="9" y="9" width="6" height="6" rx="1.5" /><path d="M7.3 7.3L9 9M16.7 7.3L15 9M7.3 16.7L9 15M16.7 16.7L15 15" /></>) },
  ];
  const row = (on: boolean, disabled?: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 48, padding: '6px 14px', boxSizing: 'border-box',
    borderRadius: 999, border: `1.5px solid ${on ? T.amber : 'transparent'}`,
    background: on ? `color-mix(in srgb, ${T.amber} 14%, transparent)` : 'transparent',
    color: disabled ? T.textMuted : on ? T.amber : T.text, opacity: disabled ? 0.6 : 1,
    cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', textAlign: 'left',
  });
  return (
    <>
      <div role="presentation" onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9997 }} />
      <div role="menu" aria-label="Välj kamera" style={{
        ...huntBarSurface(T.bg, T.border), borderRadius: 26,
        position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom, zIndex: 9998,
        width: 'min(320px, calc(100vw - 20px))', boxSizing: 'border-box', padding: 6,
        display: 'flex', flexDirection: 'column', gap: 2,
      }}>
        {onRecordVideo && (
          <button type="button" role="menuitem" onClick={() => { onRecordVideo(); onClose(); }} style={row(false)}>
            {icon(<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" fill="currentColor" /></>)}
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ fontSize: 15, fontWeight: 600 }}>Spela in video</span>
              <span style={{ fontSize: 12, color: T.textSub, fontWeight: 400 }}>Till jaktchatten och markens album</span>
            </span>
          </button>
        )}
        {items.map(it => (
          <button key={it.src} type="button" role="menuitemradio" aria-checked={active === it.src} disabled={it.disabled}
            onClick={() => { if (!it.disabled) { onSelect(it.src); onClose(); } }} style={row(active === it.src, it.disabled)}>
            {it.icon}
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ fontSize: 15, fontWeight: 600 }}>{it.label}</span>
              {it.hint && <span style={{ fontSize: 12, color: T.textSub, fontWeight: 400 }}>{it.hint}</span>}
            </span>
          </button>
        ))}
        {active && (
          <button type="button" role="menuitem" onClick={() => { onStop(); onClose(); }} style={{ ...row(false), borderTop: 'none', marginTop: 2 }}>
            {icon(<><rect x="2" y="6" width="14" height="12" rx="2" /><path d="M16 10l6-3v10l-6-3" /><path d="M3 3l18 18" /></>)}
            <span style={{ fontSize: 15, fontWeight: 600 }}>Stäng kameran</span>
          </button>
        )}
      </div>
    </>
  );
}

/**
 * Videoström i helskärm ovanpå kartan (eget lager, inte Fullscreen API – WKWebView stöder det inte pålitligt).
 * Visar samma MediaStream som rutan man tryckte på, tyst (ljudet går redan via rummet). Ett tryck på bilden,
 * svep nedåt eller Esc stänger.
 */
export function VideoFullscreen({ stream, mirror, onClose }: { stream: MediaStream; mirror?: boolean; onClose: () => void }) {
  const ref = React.useRef<HTMLVideoElement>(null);
  const startY = React.useRef<number | null>(null);
  React.useEffect(() => {
    const v = ref.current;
    if (v) { v.srcObject = stream; void v.play().catch(() => {}); }
    return () => { if (v) v.srcObject = null; };
  }, [stream]);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div role="dialog" aria-label="Video i helskärm (tryck för att stänga)"
      onPointerDown={e => { startY.current = e.clientY; }}
      onPointerUp={e => {
        const dy = startY.current == null ? 0 : e.clientY - startY.current;
        startY.current = null;
        if (Math.abs(dy) < 10 || dy > 60) onClose(); // tryck eller svep nedåt
      }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10050, background: '#000', touchAction: 'none', cursor: 'zoom-out',
        padding: 'env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px) env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px)',
        boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
      <video ref={ref} autoPlay muted playsInline style={{
        width: '100%', height: '100%', objectFit: 'contain', display: 'block',
        transform: mirror ? 'scaleX(-1)' : undefined, pointerEvents: 'none',
      }} />
    </div>
  );
}

/** Växlingsknapp i kartmeny — orange kant, tydlig av/på. */
export function MapDrawerSwitch({ checked, onChange, amber, disabled }: { checked: boolean; onChange: (v: boolean) => void; amber: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => { if (!disabled) onChange(!checked); }}
      style={{
        width: 50, height: 28, borderRadius: 14, border: `2px solid ${amber}`,
        background: checked ? amber : 'rgba(0,0,0,0.35)',
        position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0, transition: 'background 0.2s', padding: 0,
        boxShadow: checked ? '0 2px 6px rgba(0,0,0,0.2)' : 'none',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: checked ? 24 : 3, width: 20, height: 20, borderRadius: '50%',
        background: '#0a0a0a', border: `1px solid ${amber}`, boxShadow: '0 1px 3px rgba(0,0,0,0.35)', transition: 'left 0.2s ease',
      }} />
    </button>
  );
}

export function MapDrawerToggleRow({ label, checked, onChange, amber, disabled }: { label: string; checked: boolean; onChange: (v: boolean) => void; amber: string; disabled?: boolean }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
      padding: '11px 0', borderBottom: '1px solid rgba(238,143,9,0.18)',
      opacity: disabled ? 0.55 : 1,
    }}>
      <span style={{ color: amber, fontSize: 14, fontWeight: 600, flex: 1, lineHeight: 1.35 }}>{label}</span>
      <MapDrawerSwitch checked={checked} onChange={onChange} amber={amber} disabled={disabled} />
    </div>
  );
}

/** Dela-ikon: pil upp ur öppen U-behållare (export/dela) — samma stroke-stil som övriga jaktknappar. */
export function IconShareArrow({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3.5V11M8 7.5l4-4 4 4" />
      <path d="M5 12.5L5 18Q5 20 7 20H17Q19 20 19 18L19 12.5" />
    </svg>
  );
}

export function IconChatBubble({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

/** Pil i vindens riktning (blåser mot). Open-Meteo anger vindriktning som grader *från* vilken vind blåser. */
export function WindFlowArrow({ deg, size = 12 }: { deg: number; size?: number }) {
  const rot = ((deg + 180) % 360 + 360) % 360;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden
      style={{ display: 'block', transform: `rotate(${rot}deg)`, transformOrigin: '50% 50%', flexShrink: 0 }}
    >
      <path d="M12 20V8M7 13l5-7 5 7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Vänstermenyn och bottenkortet i kartläget ────────────────────────────────

/** Utfällbar sektion i vänstermenyn (en i taget styrs av föräldern). Öppen = orange rubrik. */
export function MenuSection({ title, icon, open, onToggle, children }: {
  title: string; icon?: React.ReactNode; open: boolean; onToggle: () => void; amber?: string; children: React.ReactNode;
}) {
  const T = useTheme();
  return (
    <div style={{ borderBottom: `1px solid ${T.border}` }}>
      <button type="button" onClick={onToggle} aria-expanded={open} style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        padding: '13px 18px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
        color: open ? T.accentText : T.text, fontSize: 15, fontWeight: 500, letterSpacing: '0.01em', textAlign: 'left',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{icon}{title}</span>
        <Chevron open={open} color={open ? T.amber : T.textSub} />
      </button>
      {open && <div style={{ padding: '0 18px 12px' }}>{children}</div>}
    </div>
  );
}

export function Chevron({ open, size = 14, color = 'currentColor' }: { open: boolean; size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} aria-hidden>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/** Rund segmentväljare (Av · 10 min · 1 h …): valt alternativ har orange kant, som jaktmenyns knappar. */
export function SegmentedChoice<T extends string | number>({ options, value, onChange }: {
  options: { value: T; label: React.ReactNode }[]; value: T; onChange: (v: T) => void; amber?: string;
}) {
  const th = useTheme();
  return (
    <div style={{ display: 'flex', gap: 2, padding: 2, border: `1px solid ${th.border}`, borderRadius: 999, margin: '6px 0 8px' }}>
      {options.map(o => {
        const on = o.value === value;
        return (
          <button key={String(o.value)} type="button" onClick={() => onChange(o.value)} aria-pressed={on} style={{
            flex: 1, padding: '5px 0', fontSize: 12, fontWeight: on ? 600 : 400, fontFamily: 'inherit', cursor: 'pointer',
            borderRadius: 999, border: `1.5px solid ${on ? th.amber : 'transparent'}`,
            background: on ? `color-mix(in srgb, ${th.amber} 14%, transparent)` : 'transparent', color: on ? th.accentText : th.textSub,
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}

export const TRAIL_COLORS = ['#E02424', '#EE8F09', '#facc15', '#3b82f6', '#a855f7', '#f0ebe0'];

export function ColorDots({ value, onChange, colors = TRAIL_COLORS }: { value: string; onChange: (c: string) => void; colors?: string[] }) {
  const T = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      {colors.map(c => (
        <button key={c} type="button" aria-label={`Färg ${c}`} onClick={() => onChange(c)} style={{
          width: 20, height: 20, borderRadius: '50%', background: c, cursor: 'pointer', padding: 0,
          border: '2px solid transparent', boxShadow: value === c ? `0 0 0 2px ${T.bg}, 0 0 0 3.5px ${T.amber}` : `inset 0 0 0 1px ${T.border}`,
        }} />
      ))}
    </div>
  );
}

/** Rad i en sektion som fälls ut (spårtyp, hund): titel till vänster, kort sammanfattning till höger. */
export function SubRow({ title, summary, open, onToggle, children }: {
  title: React.ReactNode; summary?: React.ReactNode; open: boolean; onToggle: () => void; children: React.ReactNode; amber?: string;
}) {
  const T = useTheme();
  return (
    <div style={{ borderBottom: `1px solid ${T.border}` }}>
      <button type="button" onClick={onToggle} aria-expanded={open} style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 2px 10px 4px',
        background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', color: T.text, fontSize: 14, fontWeight: 400, textAlign: 'left',
      }}>
        <span>{title}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, color: T.textSub, fontSize: 12.5 }}>{summary}<Chevron open={open} size={12} color={open ? T.amber : T.textSub} /></span>
      </button>
      {open && <div style={{ padding: '0 2px 10px 4px' }}>{children}</div>}
    </div>
  );
}

/** Reglagerad med förklaring under. På = orange kant och knopp (linjer, ingen fylld yta). */
export function LightToggleRow({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void; amber?: string }) {
  const T = useTheme();
  return (
    <div style={{ padding: '7px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ color: T.text, fontSize: 13.5, fontWeight: 400 }}>{label}</span>
        <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)} style={{
          width: 42, height: 25, borderRadius: 999, padding: 0, cursor: 'pointer', position: 'relative', flexShrink: 0, boxSizing: 'border-box',
          border: `1.5px solid ${checked ? T.amber : T.borderHover}`,
          background: checked ? `color-mix(in srgb, ${T.amber} 16%, transparent)` : 'transparent', transition: 'background 0.15s, border-color 0.15s',
        }}>
          <span style={{ position: 'absolute', top: 3, left: checked ? 19.5 : 3, width: 16, height: 16, borderRadius: '50%',
            background: checked ? T.amber : T.textMuted, transition: 'left 0.15s, background 0.15s' }} />
        </button>
      </div>
      {hint && <div style={{ color: T.textSub, fontSize: 11.5, fontWeight: 400, marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

/** Knapp i vänstermenyn (t.ex. "Lägg till pass"): rundad, orange kant, ingen fylld yta. */
export function MenuActionButton({ onClick, disabled, busy, children }: { onClick: () => void; disabled?: boolean; busy?: boolean; children: React.ReactNode }) {
  const T = useTheme();
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      width: '100%', textAlign: 'left', padding: '10px 16px', borderRadius: 999, border: `1.5px solid ${T.amber}`,
      background: 'transparent', color: T.text, fontWeight: 500, fontSize: 13.5,
      cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit', marginBottom: 8, opacity: busy ? 0.65 : 1,
    }}>{children}</button>
  );
}

/**
 * Bottenkort i jaktläget (som WeHunt): handtag överst, knapparna under. Dra ner (eller tryck på
 * handtaget) för att dölja knapparna så att kartan får hela skärmen; dra upp för att visa dem.
 * Läget sparas mellan jakter.
 */
export function HuntSheet({ enabled, bg, border, children }: { enabled: boolean; bg: string; border: string; children: React.ReactNode }) {
  const [collapsed, setCollapsed] = React.useState(() => {
    try { return localStorage.getItem('qvenari_hunt_sheet') === 'down'; } catch { return false; }
  });
  const [drag, setDrag] = React.useState<number | null>(null);
  const start = React.useRef<number | null>(null);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const set = (down: boolean) => {
    setCollapsed(down);
    try { localStorage.setItem('qvenari_hunt_sheet', down ? 'down' : 'up'); } catch { /* ignore */ }
  };
  if (!enabled) return <>{children}</>;
  // Nedfälld: knapparna glider under skärmkanten, bara greppet syns kvar.
  const bodyHeight = (bodyRef.current?.offsetHeight ?? 54) + 10;
  const base = collapsed ? bodyHeight : 0;
  const offset = drag == null ? base : Math.min(bodyHeight, Math.max(0, base + drag));
  return (
    <div data-hunt-sheet style={{
      position: 'fixed', left: 10, right: 10, bottom: 'calc(10px + env(safe-area-inset-bottom, 0px))', zIndex: 9996,
      maxWidth: 560, margin: '0 auto',
      transform: `translateY(${offset}px)`, transition: drag == null ? 'transform 0.25s ease' : 'none',
      ...huntBarSurface(bg, border),
      touchAction: 'none',
    }}>
      <div
        role="button"
        aria-label={collapsed ? 'Visa jaktknapparna' : 'Dölj jaktknapparna'}
        onPointerDown={e => { start.current = e.clientY; setDrag(0); (e.target as HTMLElement).setPointerCapture?.(e.pointerId); }}
        onPointerMove={e => { if (start.current != null) setDrag(e.clientY - start.current); }}
        onPointerUp={() => {
          const d = drag ?? 0;
          start.current = null;
          setDrag(null);
          if (Math.abs(d) < 6) set(!collapsed);          // tryck
          else if (d > 24) set(true);                     // dragit ner
          else if (d < -24) set(false);                   // dragit upp
        }}
        onPointerCancel={() => { start.current = null; setDrag(null); }}
        style={{ padding: '6px 0 2px', cursor: 'grab', display: 'flex', justifyContent: 'center' }}
      >
        <div style={{ width: 32, height: 4, borderRadius: 2, background: border, opacity: 0.9 }} />
      </div>
      <div ref={bodyRef} style={{ padding: '0 8px 8px' }}>{children}</div>
    </div>
  );
}
